import { useState, useSyncExternalStore } from "react";
import { SearchIcon, Settings2Icon, Rows2Icon, Columns2Icon, SplitSquareHorizontalIcon, XIcon, MenuIcon } from "lucide-react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import useUIStore from "@/stores/useUIStore";
import { subscribeMatchCount, getMatchCount } from "@/lib/searchHighlightManager";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileActionSheet } from "@/components/MobileActionSheet";
import MobileAccountMenu from "./MobileAccountMenu";

const SearchBar = () => {
  const search = useUIStore((state) => state.filters.search);
  const setFilter = useUIStore((state) => state.setFilter);
  const matchCount = useSyncExternalStore(subscribeMatchCount, getMatchCount);
  const hasSearch = search.trim().length > 0;

  return (
    <div className="flex-1 flex justify-center items-center gap-2 max-md:min-w-0">
      <SearchIcon className="bg-muted max-md:hidden" color="grey" />
      {/* label: taps anywhere on the bar (incl. the invisible match-count /
          clear-button zone on the right) focus the input */}
      <label htmlFor="search" className="flex items-center w-[80%] max-md:w-full bg-card shadow has-[:focus]:shadow-md rounded-md cursor-text">
        <Input
          type="text"
          value={search}
          id="search"
          onChange={(e) => setFilter("search", e.target.value)}
          placeholder="Search"
          className="flex-1 min-w-0 border-none bg-transparent focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0 shadow-none"
        />
        <span
          className={cn(
            "shrink-0 text-[11px] font-medium tabular-nums transition-opacity duration-200 select-none whitespace-nowrap",
            hasSearch
              ? "opacity-100 text-muted-foreground"
              : "opacity-0 pointer-events-none"
          )}
        >
          {matchCount} {matchCount === 1 ? "match" : "matches"}
        </span>
        <button
          type="button"
          onClick={() => setFilter("search", "")}
          className={cn(
            "shrink-0 p-1.5 mr-1 rounded-full transition-opacity duration-200 text-muted-foreground hover:text-foreground",
            hasSearch
              ? "opacity-100"
              : "opacity-0 pointer-events-none"
          )}
        >
          <XIcon className="h-6 w-6" />
        </button>
      </label>
    </div>
  );
};

type ViewToggleProps = {
  view: "list" | "grid" | "horizontal";
  setView: (view: "list" | "grid" | "horizontal") => void;
};

const layoutOptions = [
  { value: "grid" as const, label: "Matrix", icon: Columns2Icon },
  { value: "list" as const, label: "Vertical", icon: Rows2Icon },
  { value: "horizontal" as const, label: "Horizontal", icon: SplitSquareHorizontalIcon },
];

const ViewToggle = ({ view, setView }: ViewToggleProps) => (
  <div className="flex items-center rounded-lg border bg-background p-1 gap-1 w-full">
    {layoutOptions.map(({ value, label, icon: Icon }) => (
      <Tooltip key={value}>
        <TooltipTrigger asChild>
          <button
            onClick={() => setView(value)}
            className={cn(
              "inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-all flex-1 min-w-0 max-md:py-2.5 max-md:text-sm",
              view === value
                ? "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    ))}
  </div>
);

type FilterOptionProps = {
  label: string;
  tooltipText: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const FilterOption = ({ label, tooltipText, checked, onCheckedChange }: FilterOptionProps) => (
  <div className="flex justify-between items-center mt-2 first:mt-0 max-md:py-1.5">
    <span className="flex items-center gap-2">
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <InfoCircledIcon color="grey" className="" />
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </span>
    <Switch
      className="dark:bg-secondary"
      checked={checked}
      onCheckedChange={onCheckedChange}
    />
  </div>
);

// Shared between the desktop popover and the mobile bottom sheet
const SettingsPanel = () => {
  const filters = useUIStore((state) => state.filters);
  const setFilter = useUIStore((state) => state.setFilter);
  const layout = useUIStore((state) => state.layout);
  const setLayout = useUIStore((state) => state.setLayout);

  const setUpcomingFilter = (checked: boolean) => setFilter("upcoming", checked);
  const setPastFilter = (checked: boolean) => setFilter("past", checked);
  const setNeedsDateFilter = (checked: boolean) => setFilter("needsDate", checked);

  return (
    <>
      <p className="text-sm text-muted-foreground">Filters</p>
      <FilterOption
        label="upcoming tasks"
        tooltipText="Filter out upcoming tasks."
        checked={filters.upcoming}
        onCheckedChange={setUpcomingFilter}
      />
      <FilterOption
        label="past tasks"
        tooltipText="Filter out expired tasks."
        checked={filters.past}
        onCheckedChange={setPastFilter}
      />
      <FilterOption
        label="needs date"
        tooltipText="Filter out tasks that don't have a due date."
        checked={filters.needsDate}
        onCheckedChange={setNeedsDateFilter}
      />
      <p className="text-sm text-muted-foreground mt-4 mb-3">Layout</p>
      <ViewToggle view={layout} setView={setLayout} />
    </>
  );
};

const SettingsMenu = () => {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        <Button
          className="rounded-full hover:bg-card hover:shadow"
          size="icon"
          variant="ghost"
          aria-label="View options"
          onClick={() => setSheetOpen(true)}
        >
          <Settings2Icon className="h-5 w-5 text-muted-foreground dark:text-foreground" />
        </Button>
        <MobileActionSheet open={sheetOpen} onOpenChange={setSheetOpen} title="View options">
          <div className="px-3 pb-2 text-sm">
            <SettingsPanel />
          </div>
        </MobileActionSheet>
      </>
    );
  }

  return(
  <Popover>
    <PopoverTrigger>
      <Button className="rounded-full hover:bg-card hover:shadow" size="icon" variant="ghost">
        <Settings2Icon className="h-5 w-5 text-muted-foreground dark:text-foreground hover:text-foreground" />
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="dark:bg-card dark:border-border text-sm">
      <SettingsPanel />
    </PopoverContent>
    </Popover>
  );
};

type HeaderProps = {
  onOpenMobileSidebar?: () => void;
};

const Header = ({ onOpenMobileSidebar }: HeaderProps) => {
  return (
    <div className="flex items-center justify-center bg-muted dark:bg-background border-b h-14">
      <div className="flex-1 flex items-center max-md:flex-none">
        {/* Mobile-only: opens the lists drawer (desktop uses the floating SidebarToggle) */}
        <Button
          variant="ghost"
          size="icon"
          className="hidden max-md:inline-flex ml-1 rounded-full"
          onClick={onOpenMobileSidebar}
          aria-label="Open task lists"
        >
          <MenuIcon className="h-5 w-5 text-muted-foreground" />
        </Button>
      </div>
      <SearchBar />
      <div className="flex-1 flex justify-end items-center mr-4 gap-2 max-md:flex-none max-md:mr-1 max-md:ml-1 max-md:gap-0">
        <SettingsMenu />
        <MobileAccountMenu />
      </div>
    </div>
  );
};

export default Header;
